import { useState } from "react";
import DropBox from "../../../../component/DropBox";
import PopupPagination from "../../../../component/Pagination/popupPagination";
import {
  deletePubMission,
  endPubMission,
  SellerPubMissionCategoryFilter,
  SellerPubMissionListItemResponse,
  SellerPubMissionStatus,
} from "../../../../api/pub-mission";
import DeleteBlockedPopup from "../../../event/shared/delete-blocked-popup";
import DeleteConfirmPopup from "../../../event/shared/delete-confirm-popup";
import EndConfirmPopup from "../../../event/shared/end-confirm-popup";
import * as S from "./styles";

interface TableProps {
  list: SellerPubMissionListItemResponse[];
  pubId: number;
  categoryFilter: SellerPubMissionCategoryFilter;
  searchQuery: string;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  handleSearchQueryChange: (query: string) => void;
  handleCategoryFilterChange: (category: SellerPubMissionCategoryFilter) => void;
  onRefresh: () => Promise<void>;
  onEdit: (mission: SellerPubMissionListItemResponse) => void;
}

const CATEGORY_FILTER_LIST = [
  { name: "전체", value: SellerPubMissionCategoryFilter.ALL },
  { name: "기간 미션", value: SellerPubMissionCategoryFilter.PERIOD },
  { name: "상시미션", value: SellerPubMissionCategoryFilter.ALWAYS_ON },
];

const COLUMNS = [
  { header: "미션명", className: "name" },
  { header: "미션 구분", className: "category" },
  { header: "진행 방식", className: "type" },
  { header: "진행 기간/반복 주기", className: "period" },
  { header: "보상", className: "reward" },
  { header: "등록일", className: "createdAt" },
  { header: "상태", className: "status" },
  { header: "관리", className: "action" },
];

const getStatusClassName = (status: SellerPubMissionStatus) => {
  if (status === SellerPubMissionStatus.WAITING) return "waiting";
  if (status === SellerPubMissionStatus.IN_PROGRESS) return "in-progress";
  return "ended";
};

const Table = ({
  list,
  pubId,
  categoryFilter,
  searchQuery,
  totalPages,
  currentPage,
  setCurrentPage,
  handleSearchQueryChange,
  handleCategoryFilterChange,
  onRefresh,
  onEdit,
}: TableProps) => {
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [deletingTarget, setDeletingTarget] =
    useState<SellerPubMissionListItemResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [endingTarget, setEndingTarget] =
    useState<SellerPubMissionListItemResponse | null>(null);
  const [ending, setEnding] = useState(false);

  const onSearch = () => {
    handleSearchQueryChange(searchInput.trim());
  };

  const handleEndMission = (
    mission: SellerPubMissionListItemResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (mission.status === SellerPubMissionStatus.ENDED) return;
    setEndingTarget(mission);
  };

  const handleConfirmEnd = async () => {
    if (!endingTarget) return;

    setEnding(true);
    try {
      await endPubMission(endingTarget.id, pubId);
      setEndingTarget(null);
      await onRefresh();
    } catch (error: any) {
      alert(error?.message || "미션 종료에 실패했습니다.");
    } finally {
      setEnding(false);
    }
  };

  const handleDeleteMission = (
    mission: SellerPubMissionListItemResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (!mission.canDelete) {
      setBlockedOpen(true);
      return;
    }
    setDeletingTarget(mission);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTarget) return;

    setDeleting(true);
    try {
      await deletePubMission(deletingTarget.id, pubId);
      setDeletingTarget(null);
      await onRefresh();
    } catch (error: any) {
      alert(error?.message || "미션 삭제에 실패했습니다.");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditMission = (
    mission: SellerPubMissionListItemResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    onEdit(mission);
  };

  return (
    <S.TableWrapper>
      <S.FilterRow>
        <DropBox
          list={CATEGORY_FILTER_LIST}
          value={categoryFilter}
          setValue={handleCategoryFilterChange}
          width="140px"
        />
        <S.SearchBox>
          <input
            type="text"
            placeholder="미션명 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === "Enter") onSearch();
            }}
          />
          <div className="searchIcon" onClick={onSearch}>
            <img
              src="/image-web/timer-config/searchIcon.svg"
              alt="검색 아이콘"
            />
          </div>
        </S.SearchBox>
      </S.FilterRow>

      <S.TableContent>
        <S.TableHeader>
          {COLUMNS.map((column) => (
            <div key={column.className} className={`item ${column.className}`}>
              {column.header}
            </div>
          ))}
        </S.TableHeader>

        <S.ListWrapper>
          {list.length === 0 ? (
            <S.EmptyMessage>등록된 미션이 없습니다.</S.EmptyMessage>
          ) : (
            list.map((item) => (
              <S.TableItem key={item.id}>
                <div className="item name">{item.name}</div>
                <div className="item category">{item.categoryLabel}</div>
                <div className="item type">{item.progressTypeLabel}</div>
                <div className="item period">{item.periodOrCycleLabel}</div>
                <div className="item reward">{item.rewardLabel}</div>
                <div className="item createdAt">{item.createdAt}</div>
                <div className="item status">
                  <span
                    className={`badge ${getStatusClassName(item.status)}`}
                  >
                    {item.statusLabel}
                  </span>
                </div>
                <div className="item action">
                  <button
                    type="button"
                    className="edit"
                    onClick={(event) => handleEditMission(item, event)}
                  >
                    수정
                  </button>
                  {item.status !== SellerPubMissionStatus.ENDED && (
                    <button
                      type="button"
                      className="end"
                      onClick={(event) => handleEndMission(item, event)}
                    >
                      종료
                    </button>
                  )}
                  <button
                    type="button"
                    className="delete"
                    onClick={(event) => handleDeleteMission(item, event)}
                  >
                    삭제
                  </button>
                </div>
              </S.TableItem>
            ))
          )}
        </S.ListWrapper>
      </S.TableContent>

      {totalPages > 1 && (
        <S.PaginationBox>
          <PopupPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </S.PaginationBox>
      )}

      {blockedOpen && (
        <DeleteBlockedPopup
          targetLabel="미션"
          onClose={() => setBlockedOpen(false)}
        />
      )}

      {deletingTarget && (
        <DeleteConfirmPopup
          targetLabel="미션"
          targetName={deletingTarget.name}
          submitting={deleting}
          onClose={() => {
            if (!deleting) setDeletingTarget(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {endingTarget && (
        <EndConfirmPopup
          targetLabel="미션"
          targetName={endingTarget.name}
          submitting={ending}
          onClose={() => {
            if (!ending) setEndingTarget(null);
          }}
          onConfirm={handleConfirmEnd}
        />
      )}
    </S.TableWrapper>
  );
};

export default Table;
